import axios, { AxiosError, type AxiosProgressEvent } from 'axios';

import type { UploadsStageFile200 } from '@/generated/hooks/rain/models';

const getRainUploadUrl = (
  organization: string,
  workspace: string,
  agentId: string,
  threadId: string,
  scope?: string,
): string => {
  // `scope` is an optional path segment identifying the entity the thread hangs
  // off (e.g. `automations/{id}` or `automations/{id}/runs/{runId}`). When
  // omitted, the upload is scoped directly to the workspace agent.
  const scopeSegment = scope ? `${scope}/` : '';
  return `/api/v1/organizations/${organization}/workspaces/${workspace}/${scopeSegment}agents/${agentId}/threads/${threadId}/uploads`;
};

/**
 * Reads a `File` as a base64 data URI. Used for Rain image attachments, where
 * the client encodes images inline rather than uploading them to the thread —
 * data URIs travel directly inside `SendMessage.images[].uri`.
 */
export const readFileAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader produced non-string result'));
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });

export interface IUploadRainFileArgs {
  organization: string;
  workspace: string;
  agentId: string;
  threadId: string;
  /**
   * Optional path segment scoping the upload to an entity the thread belongs to
   * (e.g. `automations/{id}` or `automations/{id}/runs/{runId}`). Omit to scope
   * the upload directly to the workspace agent.
   */
  scope?: string;
  file: File;
  accessToken: string;
  signal?: AbortSignal;
  onProgress?: (loaded: number, total: number) => void;
}

export type IUploadRainFileResult = UploadsStageFile200;

export class RainUploadError extends Error {
  readonly httpStatus?: number;
  readonly kind: 'network' | 'aborted' | 'http';

  constructor(
    message: string,
    kind: RainUploadError['kind'],
    httpStatus?: number,
  ) {
    super(message);
    this.name = 'RainUploadError';
    this.kind = kind;
    this.httpStatus = httpStatus;
  }
}

/**
 * Multipart upload of a single file to the Rain `uploads` endpoint.
 *
 * Uses axios so the call surfaces byte-accurate upload progress via
 * `onUploadProgress`, matching the contract documented in
 * `openapi-vendored/rain/v1/uploads.openapi.yaml`. The generated orval
 * mutation doesn't expose progress, so this hand-rolled wrapper stays.
 */
export async function uploadRainFile({
  organization,
  workspace,
  agentId,
  threadId,
  scope,
  file,
  accessToken,
  signal,
  onProgress,
}: IUploadRainFileArgs): Promise<IUploadRainFileResult> {
  if (signal?.aborted) {
    throw new RainUploadError('Upload aborted before start', 'aborted');
  }

  const url = getRainUploadUrl(
    organization,
    workspace,
    agentId,
    threadId,
    scope,
  );
  const form = new FormData();
  form.append('file', file);

  try {
    const response = await axios.post<IUploadRainFileResult | string>(
      url,
      form,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal,
        responseType: 'json',
        onUploadProgress: (event: AxiosProgressEvent) => {
          if (!onProgress) return;
          const total = event.total ?? 0;
          if (total > 0) onProgress(event.loaded, total);
        },
      },
    );

    if (response.data === '' || response.data === undefined) {
      throw new RainUploadError(
        'Empty upload response body',
        'http',
        response.status,
      );
    }
    return response.data as IUploadRainFileResult;
  } catch (err) {
    if (err instanceof RainUploadError) throw err;
    if (axios.isCancel(err)) {
      throw new RainUploadError('Upload aborted', 'aborted');
    }
    if (err instanceof AxiosError) {
      if (err.code === AxiosError.ERR_CANCELED) {
        throw new RainUploadError('Upload aborted', 'aborted');
      }
      if (err.response) {
        throw new RainUploadError(
          `Upload failed with status ${err.response.status}`,
          'http',
          err.response.status,
        );
      }
      throw new RainUploadError(
        `Network error during upload: ${err.message}`,
        'network',
      );
    }
    throw new RainUploadError(
      `Failed to initialize upload: ${(err as Error).message}`,
      'network',
    );
  }
}
